/**
 * Reflux Drop-in Replacement
 *
 * Modern implementation of Reflux state management library.
 * Provides 100% API compatibility with Reflux v0.4.1 while using
 * modern JavaScript patterns internally.
 *
 * @module reflux
 * @version 1.0.0
 */

import { createAction } from './createAction.js';
import { createActions } from './createActions.js';
import { createStore, getStoreByName, getAllStoreNames, unregisterStore, STORE_INTERNAL_PROPS } from './createStore.js';

/**
 * Create a single action.
 *
 * @function createAction
 * @param {string|Object} definition - Action name or configuration object
 * @returns {Function} Action function with trigger(), listen(), and optional children
 *
 * @example
 * const logout = Reflux.createAction('logout');
 * logout(); // Trigger action
 *
 * @example
 * const login = Reflux.createAction({
 *   asyncResult: true,
 *   children: ['complete', 'fail']
 * });
 * login.listen(function(credentials) {
 *   api.login(credentials)
 *     .then(this.complete)
 *     .catch(this.fail);
 * });
 */

/**
 * Create multiple actions from array or object.
 *
 * @function createActions
 * @param {string[]|Object} definitions - Array of names or object of definitions
 * @returns {Object} Object with action names as keys
 *
 * @example
 * const actions = Reflux.createActions(['login', 'logout']);
 *
 * @example
 * const UserActions = Reflux.createActions({
 *   login: { asyncResult: true, children: ['complete', 'fail'] },
 *   logout: {}
 * });
 */

/**
 * Create a store with state and automatic action binding.
 *
 * @function createStore
 * @param {Object} definition - Store configuration
 * @returns {Object} Store instance with trigger(), listen(), and custom methods
 *
 * @example
 * const UserStore = Reflux.createStore({
 *   listenables: [UserActions],
 *
 *   init() {
 *     this.state = { user: null };
 *   },
 *
 *   onLogin() {
 *     console.log('Login triggered');
 *   },
 *
 *   onLoginComplete(user) {
 *     this.state = { user };
 *     this.trigger(this.state);
 *   },
 *
 *   getInitialState() {
 *     return this.state;
 *   }
 * });
 */

// Main Reflux API
const Reflux = {
  // Core API (fully implemented)
  createAction,
  createActions,
  createStore,

  // Store registry (for DevTools state inspection)
  getStoreByName,
  getAllStoreNames,
  unregisterStore,
  STORE_INTERNAL_PROPS,

  /**
   * Version information
   */
  version: {
    'reflux-replacement': '1.0.0'
  }
};

// Default export
export default Reflux;

// Named exports for modern import syntax
export { createAction, createActions, createStore, getStoreByName, getAllStoreNames, unregisterStore, STORE_INTERNAL_PROPS };

// CommonJS compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Reflux;
  module.exports.default = Reflux;
  module.exports.createAction = createAction;
  module.exports.createActions = createActions;
  module.exports.createStore = createStore;
  module.exports.getStoreByName = getStoreByName;
  module.exports.getAllStoreNames = getAllStoreNames;
  module.exports.unregisterStore = unregisterStore;
  module.exports.STORE_INTERNAL_PROPS = STORE_INTERNAL_PROPS;
}
